import Layout from '../../components/Layout';
import ProductCard from '../../components/ProductCard';
import DisclaimerNotice from '../../components/DisclaimerNotice';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const products = [
  {
    name: "Skin Tint",
    description: "Ultra-sheer coverage that evens out skin tone while looking like skin.",
    bestFor: "All Skin Types",
    benefits: ["Barely-there feel", "Skin-like finish", "Breathable formula"]
  },
  {
    name: "Brow Pencil",
    description: "Fine-tip pencil for natural, hair-like strokes.",
    bestFor: "Sparse Brows",
    benefits: ["Precise application", "Natural result", "Long-lasting formula"]
  },
  {
    name: "Clear Brow Gel",
    description: "Shapes and holds brows in place without color.",
    bestFor: "Full Brows",
    benefits: ["Invisible hold", "No residue", "Easy application"]
  },
  {
    name: "Lash Serum Mascara",
    description: "Defines lashes while nourishing them with conditioning ingredients.",
    bestFor: "Natural Lashes",
    benefits: ["Subtle definition", "Conditioning formula", "Easy removal"]
  },
  {
    name: "Lip Oil",
    description: "Glossy, hydrating color that looks effortless.",
    bestFor: "All Lip Types",
    benefits: ["Hydrating formula", "Glossy finish", "Subtle color"]
  },
  {
    name: "Highlighting Balm",
    description: "A subtle glow for high points of the face—no glitter, just glow.",
    bestFor: "All Skin Types",
    benefits: ["Natural glow", "No glitter", "Multi-use formula"]
  }
];

const MakeupMinimal = () => {
  return (
    <Layout>
      {/* Hero */}
      <section data-testid="minimal-hero" className="py-20 md:py-32 bg-[#F2EBE3]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link 
            to="/makeup" 
            className="inline-flex items-center gap-2 text-[#3A4D39] text-sm font-medium mb-6 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Makeup
          </Link>
          <p className="text-xs tracking-widest uppercase font-medium text-[#C88D74] mb-4">
            Makeup Style
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#1C1C1C] leading-tight mb-6">
            Minimal Makeup Guide
          </h1>
          <p className="text-base md:text-lg text-[#374151] leading-relaxed">
            The art of "no-makeup makeup"—products that enhance your natural features so subtly that no one can tell you're wearing anything at all.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-4">
            The Minimal Philosophy
          </h2>
          <p className="text-[#374151] leading-relaxed mb-4">
            Minimal makeup is about enhancing, not covering. The goal is healthy-looking skin, defined features, and a natural glow. Think "you, but better."
          </p>
          <p className="text-[#374151] leading-relaxed">
            The products that work best for this look have sheer, buildable formulas and skin-like finishes. Less product, more technique.
          </p>
        </div>
      </section>

      {/* Products */}
      <section data-testid="minimal-products" className="py-16 md:py-24 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-8">
            Recommended Products
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {products.map((product) => (
              <ProductCard key={product.name} {...product} />
            ))}
          </div>
        </div>
      </section>

      {/* How to Choose */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-6">
            How to Choose
          </h2>
          <ul className="space-y-4 text-[#374151]">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Skin-like finishes:</strong> Avoid products that look "makeup-y." Sheer, natural finishes are key.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Multi-use products:</strong> A cream blush that works on lips saves time and bag space.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Skincare first:</strong> Minimal makeup works best on well-cared-for skin.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Focus on one feature:</strong> Defined brows and a lip tint can be enough.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-[#F5F5F0]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <DisclaimerNotice variant="inline" />
        </div>
      </section>
    </Layout>
  );
};

export default MakeupMinimal;
